import { fetchTemplateByIdQuery } from 'api/queries';
import h337 from 'heatmap.js';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, withRouter } from 'react-router';
import { useGraphql } from 'utils/hooks/useGraphql';

import { ASCENDING } from 'constants/sortDirections';
import { sortRows } from 'ui/table/utils';
import { getFeatureFlags } from 'core/userContext';
import { HiddenCSVLink, StyledDivider } from 'modules/productAnalytics/Styled';
import { InfoBanner } from 'ui/banner';
import { BUTTON_VARIANTS, IconButton } from 'ui/button';
import { Overflow, Search } from 'ui/icons';
import { Menu } from 'ui/menu';
import { ALIGNMENT } from 'ui/menu/constants';
import { Table } from 'ui/table/Table';
import { getUrlParams } from 'utils/urlParams';

import { fetchCampaignLinkStatsRequest } from '../actions';
import { HEATMAP_COLS } from '../constants';
import { useTimeFiltersContext } from '../context';
import { selectCampaignLinkStats, selectSelectedVariant, selectTemplateId } from '../selectors';
import { createHeatmap, extractContentFromJqueryElem, maxBy } from '../utils';
import { HeatmapMetrics } from './HeatmapMetrics';
import { LayoutTemplate } from './LayoutTemplate';
import { NoDataCard } from './NoDataCard';
import {
  CampaignAnalyticsCardHeader,
  ClickBreakdownControls,
  HeatmapContentContainer,
  HeatmapInfoWrapper,
  HeatmapInputText,
  RawDataToggle,
  RawDataWrapper,
  StyledCampaignAnalyticsCard,
  StyledHeader,
  StyledHeatmapContainer,
  StyledHeatmapVisualization,
  StyledRows,
  TableWrapper,
} from './Styled';
import { LegacyHeatmapBanner } from './dynamicHeatmaps/LegacyHeatmapBanner';
import { useTemplateSingleContentById } from './useTemplateSingleContentById';

const getLinkStatsWithId = (linkStats) => {
  return (
    linkStats?.map((el, idx) => ({
      ...el,
      id: idx,
    })) ?? []
  );
};

export const Heatmap = ({ showLegacyHeatmapBanner = false }) => {
  const csvLink = useRef();
  const dispatch = useDispatch();
  const { search } = useLocation();
  const { campaignId, showMSO = false } = getUrlParams(search);
  const parsedShowMSO = JSON.parse(showMSO);
  const { dateParams } = useTimeFiltersContext();
  const rawLinkStats = useSelector(selectCampaignLinkStats);
  const linkStats = useMemo(
    () => (Array.isArray(rawLinkStats) ? rawLinkStats : []),
    [rawLinkStats],
  );
  const templateId = useSelector(selectTemplateId);
  const selectedVariant = useSelector(selectSelectedVariant);

  const variables = useMemo(
    () => ({
      id: parseInt(selectedVariant?.templateId || templateId, 10),
    }),
    [selectedVariant?.templateId, templateId],
  );

  const { data = {} } = useGraphql(fetchTemplateByIdQuery, {
    variables,
    skip: !variables?.id,
  });

  const { templateAndSingleContent } = useTemplateSingleContentById(variables?.id);

  const [totalCount, setTotalCount] = useState();
  const [templateHeight, setTemplateHeight] = useState(1000);
  const [templateWidth, setTemplateWidth] = useState(1000);
  const [maxCount, setMaxCount] = useState();
  const [augmentedLinkData, setAugmentedLinkData] = useState([]);
  const [searchInputValue, setSearchInputValue] = useState();
  const [tableData, setTableData] = useState([]);
  const [heatmapSortedBy, setHeatmapSortedBy] = useState('text');
  const [heatmapSortDirection, setHeatmapSortDirection] = useState(ASCENDING);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [isInfoBannerOpen, setIsInfoBannerOpen] = useState(true);
  const [urlOnlyView, setUrlOnlyView] = useState(false);
  const iframeRef = useRef();
  const heatmapVisualizationRef = useRef();
  const [sortedHeatmapRows, setSortedHeatmapRows] = useState(
    sortRows(tableData, heatmapSortedBy, heatmapSortDirection),
  );
  const overflowActions = {
    'export-heatmap-table': () => csvLink.current.link.click(),
  };

  const applyHeatmap = () => {
    if (heatmapVisualizationRef.current) {
      const heatmapConfig = {
        container: heatmapVisualizationRef.current,
        radius: 50,
        maxOpacity: 0.5,
        minOpacity: 0,
        blur: 0.75,
        legend: {
          position: 'tr',
          title: 'test',
        },
      };
      const iframe = iframeRef?.current;

      if (iframe?.contentWindow?.document?.body?.innerHTML) {
        const createdHeatmap = h337.create(heatmapConfig);
        createHeatmap(augmentedLinkData, createdHeatmap, maxCount);
      }
    }
  };

  const filterTableData = (input) => {
    setSearchInputValue(input);
    const sorted = sortRows(tableData, heatmapSortedBy, heatmapSortDirection);
    if (input) {
      const lowerInput = input?.toLowerCase();
      setSortedHeatmapRows(
        sorted.filter((el) => {
          return (
            el?.url?.toLowerCase().includes(lowerInput) ||
            el?.text?.toLowerCase().includes(lowerInput)
          );
        }),
      );
    } else {
      setSortedHeatmapRows(sorted);
    }
  };

  const toggleSetUrlOnlyView = (isActive) => {
    if (isActive) {
      setIframeLoaded(false);
    }
    setUrlOnlyView(isActive);
  };

  useEffect(() => {
    setSortedHeatmapRows(sortRows(tableData, heatmapSortedBy, heatmapSortDirection));
  }, [tableData]);

  useEffect(() => {
    if (dateParams) {
      const { templateId: selectedVariantTemplateId } = selectedVariant;
      dispatch(
        fetchCampaignLinkStatsRequest({
          campaignId,
          startDate: dateParams.startTime.toISOString(),
          endDate: dateParams.endTime.toISOString(),
          urlOnlyView,
          templateId: selectedVariantTemplateId,
        }),
      );
    }
  }, [dateParams, iframeLoaded, urlOnlyView, selectedVariant]);
  useEffect(() => {
    const iframe = iframeRef?.current;
    const augmentedLinks = [];
    const allLinks = [];
    if (linkStats && iframe?.contentWindow?.document?.body) {
      const getSum = (key) => {
        return linkStats?.map((el) => el[key])?.reduce((memo, num) => memo + num, 0) ?? 0;
      };
      const counts = {
        total: getSum('totalCount'),
        unique: getSum('uniqueCount'),
      };

      if (linkStats.length > 0) {
        setMaxCount(
          maxBy(linkStats, (link) => {
            return link.totalCount;
          }).totalCount,
        );
      }
      setTotalCount(counts.total);
      const linkStatsWithIds = getLinkStatsWithId(linkStats);
      linkStatsWithIds.forEach((linkData) => {
        const urlElem = iframe.contentWindow.document.body.querySelector(
          `[hrefIndex="${linkData.hrefIndex}"]`,
        );
        if (urlElem) {
          const extended = { ...linkData, ...extractContentFromJqueryElem(urlElem) };
          augmentedLinks.push(extended);
          allLinks.push(extended);
        } else {
          allLinks.push(linkData);
        }
      });
    } else if (linkStats) {
      allLinks.push(...getLinkStatsWithId(linkStats));
    }
    setAugmentedLinkData(augmentedLinks);
    setTableData(allLinks);
  }, [linkStats, iframeLoaded]);

  useEffect(() => {
    if (iframeLoaded) {
      const iframe = iframeRef?.current;
      if (iframe?.contentWindow?.document?.body?.innerHTML) {
        // Get rid of MSO comments so we can find links hidden in comments
        iframe.contentWindow.document.body.innerHTML = parsedShowMSO
          ? iframe.contentWindow.document.body.innerHTML
          : iframe.contentWindow.document.body.innerHTML
              .replace(/(<!--\s*\[if\s[^\]]*\]\s*?>)/g, '$1 -->')
              .replace(/(<!\s*\[endif\]\s*-->)/g, '<!-- $1');
        setTemplateHeight(
          Math.max(templateHeight, iframe?.contentWindow?.document?.body?.scrollHeight),
        );
        setTemplateWidth(iframe?.contentWindow?.document?.body?.offsetWidth);
      }
    }
  }, [iframeLoaded]);

  useEffect(() => {
    if (augmentedLinkData.length > 0) {
      applyHeatmap();
    }
  }, [augmentedLinkData]);

  const { LocaleImprovements } = useSelector(getFeatureFlags);

  /**
   * For users who are not on the `LocaleImprovements`, we use the old getTemplate query data
   */
  const contentId = LocaleImprovements
    ? templateAndSingleContent?.primaryContent?.contentId
    : data?.template?.contentId;

  return (
    <LayoutTemplate pageName="">
      {showLegacyHeatmapBanner && <LegacyHeatmapBanner />}
      {!urlOnlyView && (
        <StyledCampaignAnalyticsCard>
          <CampaignAnalyticsCardHeader data-test="card-header-heatmap">
            Heatmap
          </CampaignAnalyticsCardHeader>
          <StyledDivider />
          {isInfoBannerOpen && (
            <HeatmapInfoWrapper>
              <InfoBanner onClose={() => setIsInfoBannerOpen(false)}>
                Heatmap may not display links accurately if template contains
                conditionally-displayed links via Handlebars.
              </InfoBanner>
            </HeatmapInfoWrapper>
          )}
          {contentId && (
            <StyledHeatmapContainer id="heatmap-container">
              <HeatmapContentContainer style={{ width: templateWidth }}>
                <iframe
                  ref={iframeRef}
                  id="heatmap-iframe"
                  src={`/templates/showContent?contentId=${contentId}&messageMedium=Email&addHrefIndexAttr=true`}
                  style={{ height: templateHeight, width: templateWidth }}
                  onLoad={() => setIframeLoaded(true)}
                />
                <StyledHeatmapVisualization
                  ref={heatmapVisualizationRef}
                  data-test="heatmap-visual"
                  id="heatmap-visualization"
                  style={{ height: templateHeight, width: templateWidth }}
                />
                <HeatmapMetrics
                  augmentedLinkData={augmentedLinkData}
                  totalCount={totalCount}
                  templateHeight={templateHeight}
                  templateWidth={templateWidth}
                />
              </HeatmapContentContainer>
            </StyledHeatmapContainer>
          )}
        </StyledCampaignAnalyticsCard>
      )}
      <StyledCampaignAnalyticsCard>
        <CampaignAnalyticsCardHeader data-test="card-header-click-breakdown">
          Click breakdown{' '}
          <Menu
            handleSelectionChange={(id) => id && overflowActions[id]()}
            horizontalAlignment={ALIGNMENT.RIGHT}
            options={[{ label: 'Export table to csv', id: 'export-heatmap-table' }]}
          >
            <IconButton variant={BUTTON_VARIANTS.SECONDARY}>
              <Overflow />
            </IconButton>
          </Menu>
          <HiddenCSVLink
            data={sortedHeatmapRows}
            filename={'heatmap.csv'}
            ref={csvLink}
            target="_blank"
          />
        </CampaignAnalyticsCardHeader>
        <StyledDivider />
        <ClickBreakdownControls>
          <RawDataWrapper
            data-test="click-breakdown-raw-data"
            onClick={() => toggleSetUrlOnlyView(!urlOnlyView)}
          >
            Show raw link data{' '}
            <RawDataToggle
              data-test="click-breakdown-raw-data-toggle"
              isActive={urlOnlyView}
              onClick={toggleSetUrlOnlyView}
            />
          </RawDataWrapper>
          <HeatmapInputText
            autoComplete="off"
            data-test="heatmap-index-search"
            id="heatmap-search-input"
            onChange={filterTableData}
            placeholder={'Filter link content or URL'}
            Prefix={<Search />}
            value={searchInputValue}
          />
        </ClickBreakdownControls>
        {sortedHeatmapRows?.length > 0 ? (
          <TableWrapper>
            <Table data-test="heatmap-table">
              <StyledHeader
                columns={HEATMAP_COLS}
                sortedBy={heatmapSortedBy}
                setSortedBy={setHeatmapSortedBy}
                sortDirection={heatmapSortDirection}
                setSortDirection={setHeatmapSortDirection}
                isSticky
              />

              <StyledRows rows={sortedHeatmapRows} columns={HEATMAP_COLS} />
            </Table>
          </TableWrapper>
        ) : (
          <NoDataCard
            title="No clicks to display yet"
            subtitle="We’ll display metrics when recipients start to engage with your campaign."
          />
        )}
      </StyledCampaignAnalyticsCard>
    </LayoutTemplate>
  );
};

export default withRouter(Heatmap);
